import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Marker as GoogleMarkerType, MarkerClusterer } from '@googlemaps/markerclusterer';
import { APIProvider, Map as GoogleMap, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';

import { ObjectType, MarkerColor } from 'entities/common';
import { GOOGLE_KEY, GOOGLE_MAP_ID } from 'utils/constants';
import { MapHelper } from 'utils/helpers';
import { PAGES } from 'utils/link';
import { getZoneAiAnalysisLatest } from 'services/sensors';

type DashboardMarker = {
  key: string;
  location: google.maps.LatLngLiteral;
  color: MarkerColor;
  zoneId: number | null;
  objectId: number;
};

type AiStatusMap = Record<number, 'red' | 'yellow' | 'blue' | null>;

const COLOR_MAP: Record<MarkerColor, string> = {
  [MarkerColor.BLUE]: '#7ecbf5',
  [MarkerColor.YELLOW]: '#fff28f',
  [MarkerColor.RED]: '#ff8399',
  [MarkerColor.GRAY]: '#808080',
};

const AI_STATUS_PRIORITY: Record<string, number> = {
  red: 3,
  yellow: 2,
  blue: 1,
};

function aiStatusToMarkerColor(status: string | null): MarkerColor {
  if (status === 'red') return MarkerColor.RED;
  if (status === 'yellow') return MarkerColor.YELLOW;
  if (status === 'blue') return MarkerColor.BLUE;
  return MarkerColor.GRAY;
}

function getObjectMarkerColor(
  objectZoneIds: number[],
  aiStatusMap: AiStatusMap,
): MarkerColor {
  let worstPriority = 0;
  let worstStatus: string | null = null;

  for (const zoneId of objectZoneIds) {
    const status = aiStatusMap[zoneId];
    if (status && (AI_STATUS_PRIORITY[status] || 0) > worstPriority) {
      worstPriority = AI_STATUS_PRIORITY[status];
      worstStatus = status;
    }
  }

  return aiStatusToMarkerColor(worstStatus);
}

/** For object-level sensor click: find best zone to navigate to (red > yellow > blue) */
function getBestZoneForObject(
  objectZoneIds: number[],
  aiStatusMap: AiStatusMap,
): number | null {
  let bestZoneId: number | null = null;
  let bestPriority = 0;

  for (const zoneId of objectZoneIds) {
    const status = aiStatusMap[zoneId];
    const priority = status ? AI_STATUS_PRIORITY[status] || 0 : 0;
    if (priority > bestPriority) {
      bestPriority = priority;
      bestZoneId = zoneId;
    }
  }

  if (bestZoneId === null && objectZoneIds.length > 0) {
    bestZoneId = objectZoneIds[0];
  }

  return bestZoneId;
}

type Props = {
  objects: ObjectType[];
};

export const DashboardMap: React.FC<Props> = ({ objects }) => {
  const [aiStatusMap, setAiStatusMap] = useState<AiStatusMap>({});

  // Collect all unique zone IDs and build object→zones mapping
  const { allZoneIds, objectZonesMap } = useMemo(() => {
    const zoneIds = new Set<number>();
    const objZonesMap: Record<number, number[]> = {};

    for (const obj of objects) {
      const zoneIdsForObj: number[] = [];
      for (const zone of obj.zones) {
        zoneIds.add(zone.id);
        zoneIdsForObj.push(zone.id);
      }
      objZonesMap[obj.id] = zoneIdsForObj;
    }

    return { allZoneIds: Array.from(zoneIds), objectZonesMap: objZonesMap };
  }, [objects]);

  // Fetch AI status for all zones
  useEffect(() => {
    if (!allZoneIds.length) return;

    const statusMap: AiStatusMap = {};
    let cancelled = false;

    Promise.allSettled(
      allZoneIds.map((zoneId) =>
        getZoneAiAnalysisLatest(zoneId)
          .then((res) => {
            if (!cancelled) {
              statusMap[zoneId] = (res.data?.status?.toLowerCase() as 'red' | 'yellow' | 'blue') || null;
            }
          })
          .catch(() => {
            if (!cancelled) {
              statusMap[zoneId] = null;
            }
          }),
      ),
    ).then(() => {
      if (!cancelled) setAiStatusMap(statusMap);
    });

    return () => {
      cancelled = true;
    };
  }, [allZoneIds]);

  // Build markers from all sensors
  const markers: DashboardMarker[] = useMemo(() => {
    const result: DashboardMarker[] = [];

    for (const obj of objects) {
      const objectZoneIds = objectZonesMap[obj.id] || [];

      // Object-level sensors
      for (const sensor of obj.sensors || []) {
        const metric = sensor.latest_metrics?.[0];
        if (typeof metric?.latitude !== 'number' || typeof metric?.longitude !== 'number') continue;

        result.push({
          key: `obj-${sensor.sensor_eui}`,
          location: { lat: metric.latitude, lng: metric.longitude },
          color: getObjectMarkerColor(objectZoneIds, aiStatusMap),
          zoneId: null,
          objectId: obj.id,
        });
      }

      // Zone-level sensors
      for (const zone of obj.zones) {
        for (const sensor of zone.sensors || []) {
          const metric = sensor.latest_metrics?.[0];
          if (typeof metric?.latitude !== 'number' || typeof metric?.longitude !== 'number') continue;

          result.push({
            key: `zone-${sensor.sensor_eui}`,
            location: { lat: metric.latitude, lng: metric.longitude },
            color: aiStatusToMarkerColor(aiStatusMap[zone.id] || null),
            zoneId: zone.id,
            objectId: obj.id,
          });
        }
      }
    }

    return result;
  }, [objects, objectZonesMap, aiStatusMap]);

  if (!markers.length) return null;

  return (
    <div className={classNames('dashboard-map')}>
      <h1 className={classNames('dashboard-map__title')}>Map</h1>
      <div className={classNames('dashboard-map__content')}>
        <APIProvider apiKey={GOOGLE_KEY || ''}>
          <GoogleMap
            mapTypeControl={false}
            fullscreenControl={false}
            streetViewControl={false}
            defaultZoom={5}
            mapId={GOOGLE_MAP_ID}
            defaultCenter={{
              lat: markers[0]?.location.lat || 25,
              lng: markers[0]?.location.lng || 55,
            }}
          >
            <DashboardMarkers
              markers={markers}
              aiStatusMap={aiStatusMap}
              objectZonesMap={objectZonesMap}
            />
          </GoogleMap>
        </APIProvider>
      </div>
    </div>
  );
};

/** Inner component that renders markers (needs useMap hook inside GoogleMap context) */
const DashboardMarkers: React.FC<{
  markers: DashboardMarker[];
  aiStatusMap: AiStatusMap;
  objectZonesMap: Record<number, number[]>;
}> = ({ markers, aiStatusMap, objectZonesMap }) => {
  const map = useMap();
  const navigate = useNavigate();
  const clusterer = useRef<MarkerClusterer>(null);
  const markerRefs = useRef<Record<string, GoogleMarkerType>>({});

  const handleClick = useCallback(
    (marker: DashboardMarker) => () => {
      if (marker.zoneId) {
        navigate(PAGES.ZONE_DETAILS.replace(':id', String(marker.zoneId)));
      } else {
        const objectZoneIds = objectZonesMap[marker.objectId] || [];
        const bestZoneId = getBestZoneForObject(objectZoneIds, aiStatusMap);
        if (bestZoneId) {
          navigate(PAGES.ZONE_DETAILS.replace(':id', String(bestZoneId)));
        }
      }
    },
    [navigate, aiStatusMap, objectZonesMap],
  );

  const setMarkerRef = useCallback((ref: GoogleMarkerType | null, key: string) => {
    if (ref) {
      markerRefs.current[key] = ref;
    } else {
      delete markerRefs.current[key];
    }
    // Update clusterer when refs change
    if (clusterer.current) {
      clusterer.current.clearMarkers();
      clusterer.current.addMarkers(Object.values(markerRefs.current));
    }
  }, []);

  // Initialize clusterer
  useEffect(() => {
    if (!map) return;
    if (!clusterer.current) {
      clusterer.current = new MarkerClusterer({ map });
    }
  }, [map]);

  // Fit bounds to show all markers
  useEffect(() => {
    if (!map || !markers.length) return;

    const markerPoints = markers.map((m) => ({
      id: 0,
      zone_id: 0,
      key: m.key,
      location: m.location,
      type: m.color,
    }));

    const bounds = MapHelper.getMapSize(markerPoints);
    if (bounds) {
      const latLngBounds = MapHelper.createBounds(bounds);
      map.fitBounds(latLngBounds, 40);
      map.setCenter(MapHelper.getCenter(bounds));
    }
  }, [map, markers]);

  return (
    <>
      {markers.map((marker) => (
        <AdvancedMarker
          key={marker.key}
          onClick={handleClick(marker)}
          ref={(ref) => setMarkerRef(ref, marker.key)}
          position={marker.location}
        >
          <Pin
            background={COLOR_MAP[marker.color]}
            glyphColor="#fff"
            borderColor={COLOR_MAP[marker.color]}
          />
        </AdvancedMarker>
      ))}
    </>
  );
};
